import {
	Card,
	Space,
	Tag,
	Typography,
	Button,
	Modal,
	notification,
	Tooltip,
	Checkbox,
	Spin,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	InfoCircleOutlined,
	LoadingOutlined,
	PhoneOutlined,
} from '@ant-design/icons';
import { Lead, LeadsDashboardProps } from '@/pages/Novi/types/Leads';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { deleteLead, makeACall, bulkLeadsDelete, bulkLeadsStatusUpdate } from '@/api/novi/leads';
import LeadsInfoForm from './LeadsInfoForm';
import VirtualList from 'rc-virtual-list';
import ActionStrip from './AddLeads/UploadCSV/ActionStrip';

const { Text } = Typography;

const LeadsDashboard: React.FC<LeadsDashboardProps> = ({
	leads,
	campaignID,
	isScheduleActive,
	refetchLeads,
}) => {
	const [editingLead, setEditingLead] = useState<Lead | null>(null);
	const [deleteLeadId, setDeleteLeadId] = useState<string | null>(null);
	const [callLead, setCallLead] = useState<Lead | null>(null);
	const [selectedLeads, setSelectedLeads] = useState<Record<string, Lead>>({});
	const [selectAll, setSelectAll] = useState<Record<string, boolean>>({
		NEW: false,
		CONTACTED: false,
		PROCESSING: false,
		FAILED: false,
	});

	const scrollingRef = useRef<Record<string, boolean>>({});
	const scrollTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

	const [leadsData, setLeadsData] = useState<Record<string, any>>({
		NEW: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			totalCount: 0,
			tooltip: 'Lead which has been added recently and has not been utilized in any campaign',
			color: 'blue',
		},
		CONTACTED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			totalCount: 0,
			tooltip: 'Leads that have been successfully reached and communicated with',
			color: 'green',
		},
		PROCESSING: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			totalCount: 0,
			tooltip: 'Leads currently being processed in active campaigns or pending follow-up actions',
			color: 'orange',
		},
		FAILED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			totalCount: 0,
			tooltip:
				"Leads that couldn't be reached after maximum retry attempts or encountered communication errors",
			color: 'red',
		},
	});

	useEffect(() => {
		if (leads) {
			setLeadsData((prevData) => {
				const newData = { ...prevData };

				Object.keys(leads).forEach((status) => {
					if (newData[status] && leads[status]) {
						newData[status] = {
							...newData[status],
							...leads[status],
						};
					}
				});

				if (leads.leadStats) {
					Object.keys(leads.leadStats).forEach((status) => {
						if (newData[status]) {
							newData[status].totalCount = leads.leadStats[status] || 0;
						}
					});
				}

				return newData;
			});
		}
	}, [leads]);

	const handleScroll = useCallback(
		async (status: string, e: any) => {
			if (scrollingRef.current[status]) return;

			const { scrollHeight, scrollTop, clientHeight } = e.target;
			const threshold = scrollHeight - clientHeight;

			if (scrollTimeoutRef.current[status]) {
				clearTimeout(scrollTimeoutRef.current[status]);
			}

			scrollTimeoutRef.current[status] = setTimeout(async () => {
				if (
					scrollTop >= threshold * 0.8 &&
					leadsData[status].hasMore &&
					!leadsData[status].loading &&
					!scrollingRef.current[status]
				) {
					scrollingRef.current[status] = true;

					setLeadsData((prev) => ({
						...prev,
						[status]: { ...prev[status], loading: true },
					}));

					try {
						await refetchLeads(status, leadsData[status].nextKey);
					} catch (error) {
						notification.error({
							message: 'Loading Error',
							description: `Failed to load more ${status.toLowerCase()} leads`,
						});
					} finally {
						scrollingRef.current[status] = false;
						setLeadsData((prev) => ({
							...prev,
							[status]: { ...prev[status], loading: false },
						}));
					}
				}
			}, 200);
		},
		[leadsData, refetchLeads],
	);

	useEffect(() => {
		return () => {
			Object.values(scrollTimeoutRef.current).forEach((timeout) => {
				if (timeout) clearTimeout(timeout);
			});
		};
	}, []);

	const { mutate: immediateCall } = useMutation({
		mutationFn: makeACall,
		onSuccess: () => {
			notification.success({
				message: 'Call initiated',
				description: 'Call has been initiated successfully',
			});
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'Failed to initiate call',
			});
		},
	});

	const { mutate: deleteALead, isPending } = useMutation({
		mutationFn: deleteLead,
		onSuccess: () => {
			Object.keys(leadsData).forEach((status) => refetchLeads(status));
			setDeleteLeadId(null);
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'Failed to delete lead',
			});
		},
	});

	const handleSelectLead = useCallback((lead: Lead, checked: boolean) => {
		setSelectedLeads((prev) => {
			const newSelectedLeads = { ...prev };
			if (checked) {
				newSelectedLeads[lead.lead_id] = lead;
			} else {
				delete newSelectedLeads[lead.lead_id];
			}
			return newSelectedLeads;
		});
	}, []);

	const handleSelectAllInCategory = useCallback(
		(status: string, checked: boolean) => {
			const leadsInCategory = leadsData[status].leads;

			setSelectAll((prev) => ({
				...prev,
				[status]: checked,
			}));

			setSelectedLeads((prev) => {
				const newSelectedLeads = { ...prev };

				if (checked) {
					leadsInCategory.forEach((lead: Lead) => {
						newSelectedLeads[lead.lead_id] = lead;
					});
				} else {
					leadsInCategory.forEach((lead: Lead) => {
						delete newSelectedLeads[lead.lead_id];
					});
				}

				return newSelectedLeads;
			});
		},
		[leadsData],
	);

	const clearSelectedLeads = useCallback(() => {
		setSelectedLeads({});
		setSelectAll({
			NEW: false,
			CONTACTED: false,
			PROCESSING: false,
			FAILED: false,
		});
	}, []);

	const bulkDeleteLeads = useCallback(() => {
		const leadIds = Object.keys(selectedLeads);

		bulkLeadsDelete({
			campaign_id: campaignID,
			lead_ids: leadIds,
		})
			.then((response) => {
				const { total, successful, failed } = response.results;
				if (failed === 0) {
					notification.success({
						message: 'Success',
						description: `Successfully deleted all ${total} leads`,
					});
				} else {
					notification.warning({
						message: 'Partial success',
						description: `Deleted ${successful} leads, but ${failed} deletions failed`,
					});
				}
				Object.keys(leadsData).forEach((status) => refetchLeads(status));
				clearSelectedLeads();
			})
			.catch(() => {
				notification.error({
					message: 'Error',
					description: 'Failed to process bulk deletion',
				});
			});
	}, [selectedLeads, campaignID, refetchLeads, clearSelectedLeads]);

	const bulkUpdateStatus = useCallback(
		(newStatus: string) => {
			const leadIds = Object.keys(selectedLeads);

			bulkLeadsStatusUpdate({
				campaign_id: campaignID,
				lead_ids: leadIds,
				status: newStatus,
			})
				.then((response) => {
					const { total, successful, failed } = response.results;
					if (failed === 0) {
						notification.success({
							message: 'Success',
							description: `Successfully updated all ${total} leads to ${newStatus}`,
						});
					} else {
						notification.warning({
							message: 'Partial success',
							description: `Updated ${successful} leads to ${newStatus}, but ${failed} updates failed`,
						});
					}
					Object.keys(leadsData).forEach((status) => refetchLeads(status));
					clearSelectedLeads();
				})
				.catch(() => {
					notification.error({
						message: 'Error',
						description: 'Failed to process bulk status update',
					});
				});
		},
		[selectedLeads, campaignID, refetchLeads, clearSelectedLeads],
	);

	const renderLeadCard = useCallback(
		(lead: Lead) => (
			<Card
				size="small"
				className="hover:bg-gray-50 transition-colors"
				styles={{ body: { padding: '8px', backgroundColor: '#fafafa' } }}
				style={{ marginBottom: '8px', borderRadius: '8px' }}
			>
				<div className="space-y-1">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Checkbox
								checked={!!selectedLeads[lead.lead_id]}
								onChange={(e) => handleSelectLead(lead, e.target.checked)}
							/>
							<Text className="text-sm">{lead.phone_number}</Text>
						</div>
						<Tag
							className={`
              text-xs rounded-full border-0 px-2 py-0
              ${
								lead.type === 'ZAPIER'
									? 'bg-purple-50 text-purple-600'
									: lead.type === 'MANUAL'
										? 'bg-green-50 text-green-600'
										: 'bg-orange-50 text-orange-600'
							}
            `}
						>
							{lead.type}
						</Tag>
					</div>

					<Space size="small">
						<Button
							type="text"
							size="small"
							className="px-1 hover:bg-gray-100"
							icon={<EditOutlined className="text-blue-500 text-xs" />}
							onClick={() => setEditingLead(lead)}
						/>
						<Button
							type="text"
							size="small"
							className="px-1 hover:bg-gray-100"
							icon={<DeleteOutlined className="text-red-500 text-xs" />}
							onClick={() => setDeleteLeadId(lead.lead_id)}
						/>
						<Button
							type="text"
							size="small"
							className="px-1 hover:bg-gray-100"
							icon={<PhoneOutlined className="text-green-500 text-xs" />}
							onClick={() => setCallLead(lead)}
						/>
					</Space>
				</div>
			</Card>
		),
		[selectedLeads, handleSelectLead],
	);

	const selectedLeadsCount = Object.keys(selectedLeads).length;

	return (
		<div className="h-[530px] bg-gray-100 overflow-x-auto overflow-y-hidden relative">
			<div className="min-w-[1024px] h-full p-4">
				<div className="grid grid-cols-4 gap-4 h-full">
					{Object.entries(leadsData).map(
						([status, { leads, loading, tooltip, color, totalCount }]) => (
							<div key={status} className="flex flex-col h-full min-w-[250px]">
								<div className="flex-1 overflow-y-auto bg-white rounded-lg shadow-sm h-[calc(100%-30px)]">
									<div
										className="flex items-center justify-between mb-1 px-2 border-b border-gray-100"
										style={{ borderLeft: `4px solid ${color}` }}
									>
										<div className="flex flex-row items-center gap-2 py-3 px-1">
											<Checkbox
												checked={selectAll[status]}
												onChange={(e) => handleSelectAllInCategory(status, e.target.checked)}
											/>
											<Text strong className="text-base flex items-center gap-1">
												{status}
											</Text>
											<Tooltip title={tooltip}>
												<InfoCircleOutlined className="text-xs" />
											</Tooltip>
										</div>
										<Tag className="rounded-full text-lg font-medium px-3 py-1">
											{totalCount > 0 ? `${leads.length}/${totalCount}` : leads.length}
										</Tag>
									</div>
									<div className="p-3 relative">
										<VirtualList
											height={430}
											itemHeight={80}
											itemKey="lead_id"
											data={leads}
											onScroll={(e) => handleScroll(status, e)}
										>
											{(lead) => renderLeadCard(lead)}
										</VirtualList>
										{loading && (
											<div className="absolute bottom-0 left-0 right-0 flex items-center justify-center bg-white bg-opacity-95 p-3 border-t">
												<div className="flex items-center gap-2">
													<Spin
														indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
														size="small"
													/>
													<Text className="text-sm text-gray-600">Loading more...</Text>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						),
					)}
				</div>
			</div>

			{selectedLeadsCount > 0 && (
				<ActionStrip
					selectedCount={selectedLeadsCount}
					onDelete={bulkDeleteLeads}
					onStatusChange={bulkUpdateStatus}
					onCancel={clearSelectedLeads}
					availableStatuses={['NEW', 'CONTACTED', 'FAILED']}
				/>
			)}

			<Modal
				title="Edit Lead"
				open={!!editingLead}
				onCancel={() => setEditingLead(null)}
				footer={null}
				destroyOnClose
				width={600}
			>
				{editingLead && (
					<LeadsInfoForm
						lead={editingLead}
						onClose={() => setEditingLead(null)}
						refetchLeads={() => {
							Object.keys(leadsData).forEach((status) => refetchLeads(status));
						}}
					/>
				)}
			</Modal>

			<Modal
				open={!!deleteLeadId}
				onCancel={() => setDeleteLeadId(null)}
				footer={
					<Space size="small">
						<Button size="small" onClick={() => setDeleteLeadId(null)}>
							Cancel
						</Button>
						<Button
							size="small"
							danger
							loading={isPending}
							onClick={() => {
								deleteALead({
									campaign_id: campaignID,
									lead_id: deleteLeadId as string,
								});
							}}
						>
							Delete
						</Button>
					</Space>
				}
				width={400}
			>
				<Text className="text-sm">Are you sure you want to delete this lead?</Text>
			</Modal>

			<Modal
				open={!!callLead}
				onCancel={() => setCallLead(null)}
				footer={
					<Space size="small" style={{ borderTop: 'none' }}>
						<Button size="small" onClick={() => setCallLead(null)}>
							Cancel
						</Button>
						<Button
							size="small"
							type="primary"
							onClick={() => {
								immediateCall({
									campaign_id: campaignID,
									lead_id: callLead?.lead_id as string,
								});
								setCallLead(null);
							}}
						>
							Make a call
						</Button>
					</Space>
				}
				width={400}
			>
				<div className="p-3">
					<Text className="text-sm">Do you want to make a call to {callLead?.phone_number}?</Text>
				</div>
			</Modal>
		</div>
	);
};

export default LeadsDashboard;
